import { Box, Chip, Tab, Tabs } from '@mui/material';
import { Container } from 'Components/Container';
import { Column, Row } from 'Components/Grid';
import { useQuery } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ColumnEdit, LinkEdit, RowExhibition } from './styles';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { getPolicyById, deletePolicyVersion } from 'services/policies-service';
import { DefaultTable } from 'Components/DefaultTable';
import { tableHeader } from './table-header';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useAuthUser } from 'store/auth';
import { PageLoaderShow } from 'Components/PageLoader/Show';
import { TabPanel } from 'Components/TabPanel';
import { ModalConfirmDelete } from 'Components/ModalConfirmDelete';
import { ButtonAdd } from 'Components/Container/styles';
import { useTranslation } from 'react-i18next';

export const PoliciesShow = () => {
  const { t } = useTranslation(['policy']);
  const { id } = useParams();
  const user = useSelector(useAuthUser);
  const navigate = useNavigate();
  const [tabPosition, setTabPosition] = useState(0);
  const [open, setOpen] = useState(false);
  const [indexToDelete, setIndexToDelete] = useState(null);
  const {
    isLoading,
    error,
    data: policy,
    refetch
  } = useQuery('getPolicyById', async () => {
    const resPolicy = await getPolicyById(id);

    if (resPolicy.status === 200) return resPolicy.data;

    return null;
  });

  const onClickEdit = () => {
    navigate('/policies/edit/' + id);
  };

  const onClickAddVersion = () => {
    navigate(`/policies/${id}/versions/add`);
  };

  const onDelete = (item, index) => {
    setIndexToDelete(index);
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    setIndexToDelete(null);
  };

  const onConfirm = async () => {
    try {
      await deletePolicyVersion(id, indexToDelete);
      setOpen(false);
      setIndexToDelete(null);
      refetch();
    } catch (error) {
      console.error('Erro ao deletar versão da política:', error);
    }
  };

  return (
    <>
      {!isLoading && !error && (
        <Container
          title={`${policy?.name}`}
          breadcrumb={t('policy:show.breadcrumb', { name: policy?.name })}
          linkPage="/policies"
        >
          <Box sx={{ width: '100%' }} className="container">
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabPosition}
                onChange={(_, newValue) => setTabPosition(newValue)}
                aria-label="basic tabs example"
              >
                <Tab
                  label={t('policy:show.tab.details')}
                  id="tab-details"
                  aria-controls="tabpanel-details"
                  style={{ marginLeft: 0, marginTop: 0, width: 'fit-content' }}
                />
                <Tab
                  label={t('policy:show.tab.versions')}
                  id="tab-versions"
                  aria-controls="tabpanel-versions"
                  style={{ marginLeft: 0, marginTop: 0, width: 'fit-content' }}
                />
              </Tabs>
            </Box>
            <TabPanel value={tabPosition} index={0} id="tabpanel-details" ariaLabel="tab-details">
              <RowExhibition>
                <Column xs={3}>{t('policy:show.detail.name')}</Column>
                <Column>{policy.name}</Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('policy:show.detail.edit')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('policy:show.detail.classification')}</Column>
                <Column>{policy.classification}</Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('policy:show.detail.edit')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('policy:show.detail.userGroups')}</Column>
                <Column>
                  {policy.userGroups
                    ?.filter(item => item != null)
                    .map(userGroup => (
                      <Chip
                        key={userGroup?._id}
                        label={userGroup?.name}
                        variant="outlined"
                        color="primary"
                        sx={{
                          width: 'fit-content',
                          marginRight: '6px',
                        }}
                      />
                    ))}
                </Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('policy:show.detail.edit')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('policy:show.detail.description')}</Column>
                <Column>{policy.description}</Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('policy:show.detail.edit')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
            </TabPanel>
            <TabPanel value={tabPosition} index={1} id="tabpanel-versions" ariaLabel="tab-versions">
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <ButtonAdd
                  label={t('policy:show.addVersion')}
                  className="no-margin"
                  onClick={onClickAddVersion}
                />
              </Box>
              <Row justifyContent="center">
                <Column xs={12} className="d-flex px-0">
                  <DefaultTable
                    rows={tableHeader(t)}
                    cells={policy?.policyVersions ?? []}
                    onClickDelete={onDelete}
                    id={id}
                    userId={user.userId}
                    onClickEdit={position => {
                      navigate(`/policies/${id}/versions/edit/${position}`);
                    }}
                    link="policies"
                  />
                </Column>
              </Row>
            </TabPanel>
          </Box>
        </Container>
      )}
      {isLoading && <PageLoaderShow />}
      {error && <div>Erro</div>}
      <ModalConfirmDelete
          open={open}
          onClose={onClose}
          onConfirm={onConfirm}
          title={t('policy:show.deleteVersion.title')}
          description={t('policy:show.deleteVersion.confirm')}
        />
    </>
  );
};
